import React, {useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {Bell, Clock, CheckCheck, X} from 'lucide-react'
import {useNotifications, formatTimeAgo} from '../hooks/useNotifications'

export function NotificationBell() {
  const navigate = useNavigate()
  const {unreadCount, notifications, markAsReadApi, markAllAsReadApi, fetchUnreadCount} = useNotifications(true)
  const [open, setOpen] = useState(false)
  const bellRef = useRef<HTMLButtonElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const hoverCloseTimer = useRef<number | null>(null)
  const lastHoverFetchRef = useRef<number>(0)

  useEffect(() => {
    if (document.getElementById('notif-bell-styles')) return
    const style = document.createElement('style')
    style.id = 'notif-bell-styles'
    style.innerHTML = `
      :root {
        --notif-bg: var(--bg-surface, #ffffff);
        --notif-hover: var(--bg-hover, #f3f4f6);
        --notif-border: var(--border-color, #e5e7eb);
        --notif-text: var(--text-primary, #111827);
        --notif-muted: var(--text-secondary, #6b7280);
      }

      .notif-bell-btn {
        position: relative;
        width: 40px;
        height: 40px;
        border-radius: 10px;
        border: 1px solid var(--notif-border);
        background: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
      }
      .notif-bell-btn:hover {
        background: rgba(148, 163, 184, 0.06);
      }
      .notif-bell-btn.is-open {
        border-color: #3b82f6;
        background: rgba(59, 130, 246, 0.06);
        box-shadow: 0 6px 18px rgba(15, 23, 42, 0.22);
      }
      .notif-bell-icon-shake {
        animation: bell-shake 0.7s ease-in-out;
        transform-origin: 50% 0;
      }

      .notif-badge {
        position: absolute;
        top: -4px;
        right: -4px;
        min-width: 18px;
        height: 18px;
        padding: 0 4px;
        border-radius: 999px;
        background: #ef4444;
        color: #ffffff;
        font-size: 11px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: 2px solid var(--notif-bg);
        animation: badge-pop 0.3s ease-out;
      }

      .notif-dropdown {
        position: absolute;
        top: 48px;
        right: 0;
        width: 360px;
        max-width: 90vw;
        background: var(--notif-bg);
        border-radius: 16px;
        border: 1px solid var(--notif-border);
        box-shadow:
          0 18px 40px rgba(15, 23, 42, 0.18),
          0 0 0 1px rgba(15, 23, 42, 0.05);
        animation: dropdown-in 0.18s ease-out;
        overflow: hidden;
        z-index: 9999;
      }

      @media (max-width: 640px) {
        .notif-dropdown {
          position: fixed;
          top: 70px;
          left: 16px;
          right: 16px;
          width: auto;
          max-width: calc(100vw - 32px);
          margin: 0 auto;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
        }
        .notif-list {
          max-height: calc(100vh - 180px);
        }
      }

      .notif-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 14px 8px;
        border-bottom: 1px solid var(--notif-border);
      }
      .notif-header-left {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .notif-title {
        font-size: 14px;
        font-weight: 600;
        color: var(--notif-text);
        margin: 0;
      }
      .notif-new-pill {
        padding: 2px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        background: rgba(59, 130, 246, 0.08);
        color: #3b82f6;
      }

      .notif-header-actions {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .notif-icon-btn {
        width: 26px;
        height: 26px;
        border-radius: 999px;
        border: 1px solid var(--notif-border);
        background: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        padding: 0;
        color: var(--notif-muted);
        transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
      }
      .notif-icon-btn:hover {
        background: var(--notif-hover);
        color: var(--notif-text);
        border-color: rgba(148, 163, 184, 0.6);
      }

      .notif-list {
        max-height: 400px;
        overflow-y: auto;
        padding: 4px 4px 6px;
      }
      .notif-list::-webkit-scrollbar { width: 4px; }
      .notif-list::-webkit-scrollbar-track { background: transparent; }
      .notif-list::-webkit-scrollbar-thumb {
        background: rgba(148, 163, 184, 0.6);
        border-radius: 999px;
      }

      .notif-item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 8px 10px;
        border-radius: 10px;
        cursor: pointer;
        background: transparent;
        transition: background-color 0.15s ease;
        animation: item-slide 0.2s ease-out;
      }
      .notif-item-unread { background: rgba(59, 130, 246, 0.04); }
      .notif-item:hover { background: var(--notif-hover); }

      .notif-icon-box {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .notif-content { flex: 1 1 auto; min-width: 0; }
      .notif-message {
        font-size: 13px;
        margin: 0 0 2px;
        color: var(--notif-text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .notif-message-unread { font-weight: 600; }
      .notif-meta-row {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        color: var(--notif-muted);
      }

      .notif-priority-badge {
        padding: 2px 6px;
        border-radius: 999px;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }

      .notif-unread-dot {
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background: #3b82f6;
        flex-shrink: 0;
        margin-left: 6px;
      }

      .notif-empty { padding: 32px 16px 24px; text-align: center; }
      .notif-empty-icon-wrap {
        width: 40px;
        height: 40px;
        border-radius: 999px;
        margin: 0 auto 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(59, 130, 246, 0.08);
        color: #3b82f6;
      }
      .notif-empty-title { font-size: 14px; font-weight: 600; margin: 0 0 4px; color: var(--notif-text); }
      .notif-empty-sub { font-size: 12px; margin: 0; color: var(--notif-muted); }

      .notif-footer { border-top: 1px solid var(--notif-border); padding: 8px 12px; text-align: center; }
      .notif-footer-link { font-size: 12px; color: #3b82f6; font-weight: 500; cursor: pointer; background: transparent; border: none; }

      .notif-item:nth-child(1) { animation-delay: 0.02s; }
      .notif-item:nth-child(2) { animation-delay: 0.04s; }
      .notif-item:nth-child(3) { animation-delay: 0.06s; }
      .notif-item:nth-child(4) { animation-delay: 0.08s; }
      .notif-item:nth-child(5) { animation-delay: 0.10s; }

      @keyframes bell-shake {
        0% { transform: rotate(0deg); }
        20% { transform: rotate(14deg); }
        40% { transform: rotate(-10deg); }
        60% { transform: rotate(8deg); }
        80% { transform: rotate(-5deg); }
        100% { transform: rotate(0deg); }
      }
      @keyframes badge-pop {
        0% { transform: scale(0); opacity: 0; }
        60% { transform: scale(1.2); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes dropdown-in {
        0% { opacity: 0; transform: translateY(-8px) scale(0.97); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes item-slide {
        0% { opacity: 0; transform: translateX(-6px); }
        100% { opacity: 1; transform: translateX(0); }
      }
    `
    document.head.appendChild(style)
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!open) return
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const list = notifications.slice(0, 5)
  const computedUnread = unreadCount ?? 0

  const openNow = () => {
    if (hoverCloseTimer.current) {
      window.clearTimeout(hoverCloseTimer.current)
      hoverCloseTimer.current = null
    }
    setOpen(true)

    // Fresh unread badge on hover (debounced).
    const now = Date.now()
    if (now - lastHoverFetchRef.current > 25000) {
      lastHoverFetchRef.current = now
      void fetchUnreadCount()
    }
  }

  const closeSoon = () => {
    if (hoverCloseTimer.current) window.clearTimeout(hoverCloseTimer.current)
    hoverCloseTimer.current = window.setTimeout(() => setOpen(false), 140)
  }

  return (
    <div style={{position: 'relative', display: 'inline-flex'}}>
      <button
        ref={bellRef}
        type='button'
        className={`notif-bell-btn ${open ? 'is-open' : ''}`}
        aria-label='Notifications'
        onMouseEnter={openNow}
        onMouseLeave={closeSoon}
        onClick={() => setOpen((p) => !p)}
      >
        <Bell
          size={17}
          color={open ? '#3b82f6' : 'var(--notif-muted)'}
        />
        {computedUnread > 0 && (
          <span className='notif-badge'>{computedUnread > 99 ? '99+' : String(computedUnread)}</span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className='notif-dropdown'
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
        >
          <div className='notif-header'>
            <div className='notif-header-left'>
              <h3 className='notif-title'>Notifications</h3>
              {computedUnread > 0 && (
                <span className='notif-new-pill'>{computedUnread} new</span>
              )}
            </div>
            <div className='notif-header-actions'>
              <button
                type='button'
                className='notif-icon-btn'
                title='Mark all read'
                onClick={async () => {
                  setOpen(false)
                  await markAllAsReadApi?.()
                }}
              >
                <CheckCheck size={14} />
              </button>
              <button
                type='button'
                className='notif-icon-btn'
                onClick={() => setOpen(false)}
                title='Close'
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <div className='notif-list'>
            {list.length === 0 && (
              <div className='notif-empty'>
                <div className='notif-empty-icon-wrap'>
                  <Bell size={18} />
                </div>
                <p className='notif-empty-title'>No notifications</p>
                <p className='notif-empty-sub'>You’re all caught up</p>
              </div>
            )}
            {list.map((n: any) => {
              const isUnread = !n.isRead
              return (
                <div
                  key={n.id}
                  className={`notif-item ${isUnread ? 'notif-item-unread' : ''}`}
                  onClick={() => {
                    setOpen(false)
                    if (!n.isRead) {
                      void markAsReadApi?.(n.id)
                    }
                    navigate('/notifications')
                  }}
                >
                  <div className='notif-content'>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: isUnread ? 700 : 500,
                        color: 'var(--notif-text)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        marginBottom: 2,
                      }}
                    >
                      {n.title || 'Notification'}
                    </div>
                    <p className='notif-message'>{n.message || ''}</p>
                    <div className='notif-meta-row'>
                      <Clock size={11} />
                      <span>{formatTimeAgo(n.createdAt)}</span>
                    </div>
                  </div>
                  {isUnread && <span className='notif-unread-dot' />}
                </div>
              )
            })}
          </div>

          <div className='notif-footer'>
            <button
              type='button'
              className='notif-footer-link'
              onClick={() => {
                setOpen(false)
                navigate('/notifications')
              }}
            >
              View all notifications &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

